import React, { FC, PropsWithChildren } from 'react'
import { Card, CardContent, CardMedia, Grid, Typography } from '@mui/material'
import { IHeaderElement } from '@norstella/nxp-sso-web/utils/Forms'
import { ApplicationAlert } from '@norstella/nxp-sso-web/components/ApplicationAlert'
import styles from '@norstella/nxp-sso-web/components/ApplicationCard.module.scss'
import { IBrandingPageSection, useBranding } from '@norstella/nxp-sso-web/hooks/useBranding'
import { KNOWN_PAGES } from '@norstella/nxp-sso-web/utils/pages'
import parse from 'html-react-parser'
import { useSEO } from '@norstella/nxp-sso-web/hooks/useSEO'

export interface IApplicationCardProps {
  header: IHeaderElement,
  description?: IHeaderElement,
  className?: string,
  pageName?: KNOWN_PAGES,
}

const ApplicationCard: FC<PropsWithChildren<IApplicationCardProps>> = ({
  header,
  description,
  children,
  className = '',
  pageName,
}) => {
  const [, branding] = useBranding()
  const themeClass = `theme-${branding.themeId}`

  const headerClasses = [
    'logo-container',
    styles.cardHeaderContainer,
    header.isGeneralBrandingUsed ? styles.generalBrandingHeader : '',
  ].join(' ')

  const getCustomHeaderContent = (pageName?: KNOWN_PAGES) => {
    if (!pageName) {
      return {
        title: null,
        description: null,
      }
    }

    const subHeaderOptions = (branding?.[pageName] as IBrandingPageSection)?.subHeader
    if (!subHeaderOptions) {
      return {
        title: null,
        description: null,
      }
    }

    const title = subHeaderOptions.title
    const description = parse(
      `<div>${subHeaderOptions.description ?? ''}</div>`,
      { trim: true },
    ) as JSX.Element

    return { title, description }
  }

  const { title: customTitle, description: customDescription } = getCustomHeaderContent(pageName)
  const useDefaultHeader = !customTitle

  const renderDefaultHeader = () => (
    <>
      <Typography variant="h1" component="h1">
        {header.text}
      </Typography>
      {description && (
        <Typography variant={description.variant} component="p">
          {description.text}
        </Typography>
      )}
    </>
  )

  const renderCustomHeader = () => (
    <>
      <Typography variant="h1" component="h1">
        {customTitle}
      </Typography>
      {customDescription}
    </>
  )

  const getSeoTitle = (): string | null => {
    if (!pageName) {
      return null
    }
    return (branding?.[pageName] as IBrandingPageSection)?.seo?.title ?? null
  }

  useSEO({
    title: getSeoTitle(),
  })

  return (
    <Grid
      className={`${styles.cardRoot} ${className}`}
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      spacing={5}
    >
      <Grid item className={themeClass}>
        <Grid container direction="column" alignItems="center" justifyContent="center">
          <Card className={styles.card}>
            <div className={headerClasses} style={{ backgroundColor: header.bgColor }}>
              <CardMedia
                className={styles.cardHeaderMedia}
                component="img"
                image={header.image}
              />
            </div>
            <CardContent>
              <ApplicationAlert className={styles.cardAlert} />
              {useDefaultHeader ? renderDefaultHeader() : renderCustomHeader()}
              {children}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ApplicationCard
